import axios from "axios";
import { quimeraUrl } from "../Router/constant";

export const QUIMERA_TOKEN_STORAGE_KEY = "quimeraAuthToken";
const LEGACY_QUIMERA_TOKEN_STORAGE_KEY = "quimeraAccessToken";

const normalizeToken = (value) =>
  String(value || "").trim().replace(/^(?:Bearer\s+)+/i, "").trim();

const getApiRoot = () => {
  const baseUrl = String(quimeraUrl || "").trim().replace(/\/+$/, "");
  if (!baseUrl) {
    throw new Error("Quimera API URL is missing. Configure REACT_APP_QUIMERA_API_URL in .env and restart the development server.");
  }
  return /\/api$/i.test(baseUrl) ? baseUrl : `${baseUrl}/api`;
};

const authConfig = (token) => {
  const accessToken = normalizeToken(token) || getSavedQuimeraToken();
  if (!accessToken) {
    throw new Error("Quimera authentication token is missing. Please connect your Quimera account again.");
  }
  return { headers: { Token: accessToken, "Content-Type": "application/json" } };
};

const loginQuimera = async (credentials) =>
  axios.post(`${getApiRoot()}/auth/login`, credentials);

const verifyQuimeraToken = async (token) =>
  axios.get(`${getApiRoot()}/auth/verify-token`, authConfig(getSavedQuimeraToken() || token));

const getQuimeraOrders = async ({ status = "", offset = 1, limit = 8, token } = {}) => {
  const query = new URLSearchParams({
    status: String(status || ""),
    offset: String(offset),
    limit: String(limit),
  });
  return axios.get(`${getApiRoot()}/order?${query.toString()}`, authConfig(getSavedQuimeraToken() || token));
};

const getQuimeraOrder = async (id, token) =>
  axios.get(`${getApiRoot()}/order/${encodeURIComponent(id)}`, authConfig(getSavedQuimeraToken() || token));

const createQuimeraOrder = async (payload, token) =>
  axios.post(`${getApiRoot()}/order`, payload, authConfig(getSavedQuimeraToken() || token));

const getAccessToken = (response) => {
  const body = response?.data ?? response;
  const candidates = [
    body?.accessToken,
    body?.quimeraToken,
    body?.access_token,
    body?.idToken,
    body?.id_token,
    body?.token,
    body?.jwt,
    body?.data?.accessToken,
    body?.data?.quimeraToken,
    body?.data?.access_token,
    body?.data?.idToken,
    body?.data?.id_token,
    body?.data?.token,
    body?.data?.jwt,
    body?.data?.data?.accessToken,
    body?.data?.data?.quimeraToken,
    body?.data?.data?.access_token,
    body?.data?.data?.token,
    body?.data?.data?.jwt,
    body?.tokens?.accessToken,
    body?.data?.tokens?.accessToken,
  ];
  const token = candidates.find((candidate) => typeof candidate === "string" && candidate.trim());
  return normalizeToken(token);
};

const tokenIsRejected = (response) => {
  const body = response?.data?.data || response?.data || response || {};
  return (
    body?.valid === false ||
    body?.verified === false ||
    body?.isValid === false ||
    body?.authenticated === false ||
    body?.success === false
  );
};

const getSavedQuimeraToken = () => {
  if (typeof window === "undefined") return "";
  const savedToken = normalizeToken(window.localStorage.getItem(QUIMERA_TOKEN_STORAGE_KEY));
  if (savedToken) return savedToken;

  // Migrate tokens saved by earlier versions of the dashboard.
  const legacyToken = normalizeToken(window.localStorage.getItem(LEGACY_QUIMERA_TOKEN_STORAGE_KEY));
  if (legacyToken) {
    saveQuimeraToken(legacyToken);
    return legacyToken;
  }
  return "";
};

const saveQuimeraToken = (token) => {
  if (typeof window === "undefined") return;
  const accessToken = normalizeToken(token);
  if (!accessToken) return;
  window.localStorage.setItem(QUIMERA_TOKEN_STORAGE_KEY, accessToken);
  window.localStorage.removeItem(LEGACY_QUIMERA_TOKEN_STORAGE_KEY);
};

const clearQuimeraToken = () => {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(QUIMERA_TOKEN_STORAGE_KEY);
    window.localStorage.removeItem(LEGACY_QUIMERA_TOKEN_STORAGE_KEY);
  }
};

export default {
  loginQuimera,
  verifyQuimeraToken,
  getQuimeraOrders,
  getQuimeraOrder,
  createQuimeraOrder,
  getAccessToken,
  tokenIsRejected,
  getSavedQuimeraToken,
  saveQuimeraToken,
  clearQuimeraToken,
};
